import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Dialog, Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
} from "./AddEditMailToken.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditMailToken = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedMailToken,
  } = props;

  const [isMailTokenCreating, setIsMailTokenCreating] = useState(false);
  const [createMailTokenAPIError, setCreateMailTokenAPIError] = useState("");

  const [formData, setFormData] = useState({
    name: {
      required: true,
      value: selectedMailToken?.name ?? "",
      error: "",
    },
    description: {
      required: false,
      value: selectedMailToken?.description ?? "",
      error: "",
    },
  });

  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onDescriptionChange = (e) => {
    setFormData({
      ...formData,
      description: {
        value: e.target.value,
        error: "",
        required: false,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      name: formData.name.value,
      description: formData.description.value,
      isActive: 1,
    };

    if (isEditMode && id) {
      data.id = id;
    }

    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsMailTokenCreating(true);
      setCreateMailTokenAPIError("");

      // Create mail token
      if (!id && !isEditMode) {
        axios
          .post("/general/mailTokens", getFormData())
          .then(() => {
            setIsMailTokenCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsMailTokenCreating(false);
            setCreateMailTokenAPIError(
              error.response?.data?.message ?? t("GENERAL_ERROR")
            );
          });
      }

      // Update mail token
      if (id && isEditMode) {
        axios
          .put(`/general/mailTokens/${id}`, getFormData())
          .then(() => {
            setIsMailTokenCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsMailTokenCreating(false);
            setCreateMailTokenAPIError(
              error.response?.data?.message ?? t("GENERAL_ERROR")
            );
          });
      }
    }
  };

  return (
    <Dialog width="50rem">
      <div>
        <HeaderWrapper>
          <Title>{t("ADMINISTRATION_EMAIL_TOKEN_FORM_TITLE")}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close mail token popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>

        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <GridRow column="1fr">
                  <TextInput
                    id="token-name"
                    type="text"
                    label={t("EMAIL_TOKEN_NAME")}
                    autoComplete="off"
                    isFloat
                    required
                    value={String(formData.name.value)}
                    onChange={onNameChange}
                    containerStyles={{ marginTop: "21px" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={
                      formData.name.error !== ""
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formData.name.error !== ""
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formData.name.error}
                    placeholder={t("EMAIL_TOKEN_NAME")}
                  />
                </GridRow>

                <GridRow column="1fr">
                  <TextInput
                    id="email-description"
                    type="textarea"
                    label={t("EMAIL_TOKEN_DESCRIPTION")}
                    autoComplete="off"
                    isFloat
                    value={String(formData.description.value)}
                    onChange={onDescriptionChange}
                    containerStyles={{ marginTop: "21px" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={
                      formData.description.error !== ""
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formData.description.error !== ""
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formData.description.error}
                    placeholder={t("EMAIL_TOKEN_DESCRIPTION")}
                    rows={6}
                  />
                </GridRow>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createMailTokenAPIError && (
            <div style={{ color: "red", marginRight: "1rem" }}>
              {createMailTokenAPIError}
            </div>
          )}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isMailTokenCreating && <ClipLoader size={15} color="#fff" />}
              {!isMailTokenCreating && t("VALIDATE")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditMailToken.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
  selectedMailToken: PropTypes.object,
};

AddEditMailToken.defaultProps = {
  id: null,
  isEditMode: false,
  selectedMailToken: null,
};

export default withTranslation()(AddEditMailToken);
